import { createSignal, Show, useContext } from 'solid-js';
import { StyledCertificateCard, StyledCertificateCardOverlay, StyledCertificateCardText, StyledCertificateIconContainer, StyledCertificateIconContainerInner, StyledDownloadButtonContainer, StyledLoadingSpinnerContainer, StyledOutput } from './certificate-card.styles';
import { CourseCardData } from '../course-card/course-card';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { CertificateIcon } from '../icons-library/certificate-icon';
import { gql } from 'graphql-request';
import { CourseAndUsersData } from '../course/course-types';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { ErrorCatcher } from '../../tools/error-catcher';
import { logErrorToServer } from '../../tools/log-error-to-server';

export type Certificate = Omit<CourseAndUsersData, 'coverImageUrl'>;

type CertificateCardProps = {
    data: CourseCardData;
    downloadInProgress: boolean;
    setDownloadInProgress: (value: boolean) => void;
};

type Status =
    ''
    | 'error'
    | 'generating'
    | 'retrying-generating'
    | 'server-busy';

const GET_CERTIFICATE_FILE_QUERY = gql`
    query GenerateCertificateFile (
        $domain: String!,
        $environment: String!,
        $certificateData: String!,
    ) {
        resource: generateCertificateFile (
            domain: $domain, 
            environment: $environment,
            certificateData: $certificateData,
        ) {
            buffer, 
            success,
            message,
            maxConcurrent,
        }
    }
`;

const maxRetryAttempts = 2;

export const CertificateCard = (props: CertificateCardProps) => {
    const { 
        localize,
        siteInfo,
        deploymentEnvironment,
        graphQlClient,
        viewport,
    } = useContext(AppContext);

    const [ generationRetryCount, setGenerationRetryCount ] = createSignal(0);
    const [ status, setStatus ] = createSignal<Status>('');

    const failedAttempt = () => {
        setStatus('error');
        props.setDownloadInProgress(false);
    };

    const requestFile = async (retryCount: number = 0) => {
        props.setDownloadInProgress(true);

        try {
            setGenerationRetryCount(retryCount);
            setStatus('generating');

            const file = await fetchFile();
            if (!file) return;

            triggerDownload(file);

        } catch (err: any) {
            console.log('Certificate file request error: ', err);
            failedAttempt();
            return;
        }
    };

    const fetchFile = async () => {
        try {
            const variables = {
                domain: siteInfo?.currentDomain,
                environment: deploymentEnvironment,
                certificateData: JSON.stringify(props.data),
            };

            const response = await graphQlClient(GET_CERTIFICATE_FILE_QUERY, variables, false);

            if (!response || !response.resource) {
                throw new Error('No response from server');
            }

            const retryAttemptsReached = generationRetryCount() >= maxRetryAttempts;

            if (response.resource.maxConcurrent && !retryAttemptsReached) {
                // Retry download twice if the file service is busy
                setStatus('retrying-generating');
                const count = generationRetryCount() + 1;
                setGenerationRetryCount(count);

                setTimeout(() => {
                    requestFile(count);
                }, 8000);

                return;
            }

            if (response.resource.maxConcurrent && retryAttemptsReached) {
                setStatus('server-busy');
                return;
            }

            if (!response.resource.success) {
                throw new Error(response.resource.message);
            }

            return response.resource.buffer;
        } catch (err: any) {
            console.log('Certificate fetch file error: ', err);
            failedAttempt();

            logErrorToServer({
                message: `Unable to fetch certificate file: ${err.message}`,
                url: window.location.href,
                componentName: 'CertificateCard',
                error: err,
                viewport,
            });

            return;
        }
    };

    const triggerDownload = (blob: string) => {
        const fileName = `${props.data.curriculum?.name} - ${props.data.name} - ${props.data.completedDate}`;

        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${blob}`;
        link.download = `${fileName}.pdf`;
        link.click();

        props.setDownloadInProgress(false);
        setStatus('');
    };

    const generating = () => status() === 'generating' || status() === 'retrying-generating';
    const errorGenerating = () => status() === 'error' || status() === 'server-busy';
    const generationDisabled = () => generating() || props.downloadInProgress;

    const TopOfCard = () => (
        <StyledCertificateCardOverlay generating={generating()}>

            <StyledCertificateIconContainer generating={generating()} error={errorGenerating()}>
                <StyledCertificateIconContainerInner generating={generating()} error={errorGenerating()}>
                    <CertificateIcon />
                </StyledCertificateIconContainerInner>
            </StyledCertificateIconContainer>

            <Show when={generating()}>
                <StyledLoadingSpinnerContainer>
                    <LoadingSpinner thickness='0.55rem' />
                </StyledLoadingSpinnerContainer>
            </Show>

        </StyledCertificateCardOverlay>
    );

    const CurriculumName = () => (
        <Heading tag="h3" variant="smallPDF" lineLimit={1} color={{ name: 'gray', color: 'gray' }}>
            {props.data.curriculum?.name}
        </Heading>
    );

    const CourseName = () => (
        <Heading tag="h3" variant="tinyGray" lineLimit={2}>
            {props.data.name}
        </Heading>
    );

    const CompletedDate = () => (
        <Text fontSize='smaller' fontStyle='italic' color='gray' lineLimit={1}>
            {`${localize('completed', 'Completed')}: ${props.data.completedDate}`}
        </Text>
    );

    const FileDownloadStatusMessages = () => (
        <>
            <StatusGenerating />
            <StatusRetrying />
            <StatusServerBusy />
            <StatusError />
        </>
    );

    const StatusGenerating = () => (
        <Show when={status() === 'generating'}>
            <StyledOutput>
                <Text color='black' fontSize='normalPDF'  lineLimit={3}>
                    {localize(
                        'certificate-generating-file',
                        'Creating and downloading certificate...'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusRetrying = () => (
        <Show when={status() === 'retrying-generating'}>
            <StyledOutput>
                <Text color='black' fontSize='normalPDF'  lineLimit={3}>
                    {localize(
                        'certificate-file-generator-busy-retrying',
                        'Certificate service busy, retrying...'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusServerBusy = () => (
        <Show when={status() === 'server-busy'}>
            <StyledOutput error>
                <Text color='black' fontSize='normalPDF' lineLimit={3}>
                    {localize(
                        'certificate-file-generator-at-capacity',
                        'Certificate service at capacity, please try again later.'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusError = () => (
        <Show when={status() === 'error'}>
            <StyledOutput error>
                <Text color='black' fontSize='normalPDF' lineLimit={3}>
                    {localize(
                        'certificate-download-error',
                        'An error has occurred, try again or contact support if the problem persists.'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const DownloadButton = () => (
        <StyledDownloadButtonContainer 
            disabled={generationDisabled()}
            pushDown={generating() || status() === 'error' || status() === 'server-busy'}
        >
            <Heading tag='span' variant='smallRedPDF' noBlockSpacing>
                {localize('download', 'Download')}
            </Heading>
        </StyledDownloadButtonContainer>
    );

    return (
        <ErrorCatcher componentName='CertificateCard' logErrorToServer={true}>
            <StyledCertificateCard 
                disabled={generationDisabled()}
                onClick={() => requestFile()}
                aria-label={localize('download-certificate', 'Download Certificate')}
            >
                <TopOfCard />

                <StyledCertificateCardText showGeneratorStatusText={generating() || errorGenerating()}>
                    <CurriculumName />
                    <CourseName />
                    <CompletedDate />

                    <FileDownloadStatusMessages />

                    <DownloadButton />
                </StyledCertificateCardText>

            </StyledCertificateCard>
        </ErrorCatcher>
    );
};

